<template>
  <div
    v-if="pageReady"
    class="cases"
  >
    <!-- ====== 1. Hero Section ====== -->
    <section class="cases-hero">
      <div class="cases-hero__inner">
        <div class="cases-hero__content">
          <span class="cases-hero__badge">{{ $t('真实卖家 · 真实增长') }}</span>
          <h1 class="cases-hero__title">
            {{ $t('全成功案例') }}<br>{{ $t('品牌出海，从Taoify开始') }}
          </h1>
          <p class="cases-hero__subtitle">
            {{ $t('50,000+ 跨境卖家信赖的独立站平台，轻松打造高转化品牌官网') }}
          </p>
          <div class="cases-hero__stat">
            <span class="cases-hero__stat-icon">{{ heroImages.statIcon }}</span>
            <span class="cases-hero__stat-num">50000+</span>
            <span class="cases-hero__stat-label">{{ $t('卖家共同选择') }}</span>
          </div>
        </div>

        <div class="cases-hero__visual">
          <img :src="require('@/assets/images/cases/hero-right.png')" alt="" class="cases-hero__visual-img" loading="lazy">
        </div>
      </div>
    </section>

    <!-- ====== 2. Featured Cases Header + Filter ====== -->
    <section class="cases-section">
      <h2 class="cases-section__title">
        {{ $t('精选标杆案例') }}
      </h2>
      <p class="cases-section__subtitle">
        {{ $t('来自不同行业的成功故事，看他们如何用Taoify抢占全球市场') }}
      </p>
      <div class="cases-filter">
        <button
          v-for="cat in categories"
          :key="cat.id"
          class="cases-filter__tab"
          :class="{ 'cases-filter__tab--active': activeCategory === cat.id }"
          @click="onFilterChange(cat.id)"
        >
          {{ $t(cat.nameKey) }}
        </button>
      </div>
    </section>

    <!-- ====== 3. Preview Image Strip ====== -->
    <section class="cases-preview">
      <div
        ref="previewStrip"
        class="cases-preview__strip"
        @scroll="onStripScroll"
      >
        <div class="cases-preview__track">
          <div
            v-for="(img, idx) in previewImages"
            :key="idx"
            class="cases-preview__card"
            :class="{ 'cases-preview__card--active': activePreviewIdx === idx }"
            @mouseenter="onPreviewHover(idx, $event)"
            @mouseleave="onPreviewLeave(idx)"
          >
            <div
              :ref="'cardScroll-' + idx"
              class="cases-preview__card-inner"
            >
              <img
                :src="img.src"
                :alt="img.alt"
                class="cases-preview__img"
                loading="lazy"
                @error="onImageError('preview-' + idx)"
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Custom Slider -->
      <div class="cases-slider">
        <button
          class="cases-slider__arrow cases-slider__arrow--left"
          aria-label="previous"
          @click="onSlidePrev"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="11"
              stroke="#7331ff"
              stroke-width="2"
              fill="none"
            />
            <path
              d="M14 8l-4 4 4 4"
              stroke="#7331ff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          ref="sliderTrack"
          class="cases-slider__track"
          @mousedown="onSliderDragStart"
          @touchstart.prevent="onSliderDragStart"
        >
          <div
            class="cases-slider__fill"
            :style="{ width: sliderPercent + '%' }"
          />
          <div
            class="cases-slider__thumb"
            :style="{ left: sliderPercent + '%' }"
            @mousedown.stop="onThumbDragStart"
            @touchstart.stop.prevent="onThumbDragStart"
          />
        </div>
        <button
          class="cases-slider__arrow cases-slider__arrow--right"
          aria-label="next"
          @click="onSlideNext"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="11"
              stroke="#7331ff"
              stroke-width="2"
              fill="none"
            />
            <path
              d="M10 8l4 4-4 4"
              stroke="#7331ff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </section>

    <!-- ====== 4. Feedback Tagline ====== -->
    <section class="cases-feedback">
      <span class="cases-feedback__tagline">
        {{ $t('听听他们怎么说 · 简单、高效、持续增长') }}
      </span>
    </section>

    <!-- ====== 6. User Reviews + Rating (独立用户评价板块) ====== -->
    <section class="cases-reviews">
      <h3 class="cases-reviews__heading">
        {{ $t('卖家真实反馈') }}
      </h3>

      <div class="cases-reviews__grid">
        <!-- Rating summary card -->
        <div class="cases-review-card cases-review-card--rating">
           <img
              :src="ratingData.icon"
              alt=""
             class="cases-review-card__rating-icon"
            >

          <span class="cases-review-card__rating-label">{{ $t('用户评价') }}</span>
          <div class="cases-review-card__rating-stars">
            <img
              :src="ratingData.starImage"
              alt=""
              class="cases-review-card__rating-star-img"
            >
            <span class="cases-review-card__rating-num">{{ ratingData.rating }}</span>
            <span class="cases-review-card__rating-max">/{{ ratingData.ratingMax }}</span>
          </div>
        </div>

        <!-- Testimonial cards -->
        <div
          v-for="(t, idx) in testimonials"
          :key="idx"
          class="cases-review-card"
          :class="'cases-review-card--' + t.variant"
        >
          <div class="cases-review-card__header">
            <img
              class="cases-review-card__avatar"
              :src="t.avatar"
              :alt="$t(t.nameKey)"
              loading="lazy"
              @error="onImageError('avatar-' + idx)"
            >
            <div class="cases-review-card__name-row">
              <span class="cases-review-card__name">{{ $t(t.nameKey) }}</span>
              <img
                class="cases-review-card__stars"
                :src="t.stars"
                alt=""
                loading="lazy"
              >
            </div>
            <img
              class="cases-review-card__quote-icon"
              :src="t.quoteIcon"
              alt=""
              loading="lazy"
            >
          </div>
          <p class="cases-review-card__quote">
            {{ $t(t.quoteKey) }}
          </p>
        </div>
      </div>
    </section>

    <!-- ====== 7. CTA Section ====== -->
    <section class="cases-cta">
      <div class="cases-cta__inner">
        <h2 class="cases-cta__title">
          {{ $t('开启你的品牌出海之旅') }}
        </h2>
        <p class="cases-cta__subtitle">
          {{ $t('无需代码，模板精美，一站式跨境解决方案') }}
        </p>
        <button
          class="cases-cta__btn"
          @click="onCTA"
        >
          <span>{{ $t('免费试用') }}</span>
          <span class="cases-cta__btn-icon">{{ heroImages.statIcon }}</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
  export default {
    name: 'Cases',
    data () {
      return {
        pageReady: true,
        activeCategory: 'all',

        // ---- Hero Images ----
        heroImages: {
          statIcon: '🏆',
        },

        // ---- Preview Image Strip ----
        previewImages: [
          { src: require('@/assets/images/cases/FeaturedCases-1.png'), alt: 'Featured 1' },
          { src: require('@/assets/images/cases/FeaturedCases-2.png'), alt: 'Featured 2' },
          { src: require('@/assets/images/cases/FeaturedCases-3.png'), alt: 'Featured 3' },
          { src: require('@/assets/images/cases/FeaturedCases-4.png'), alt: 'Featured 4' },
          { src: require('@/assets/images/cases/FeaturedCases-5.png'), alt: 'Featured 5' },
        ],
        activePreviewIdx: 2, // 默认第3张居中 (0-indexed)
        cardBaseWidth: 360,
        expandedCardWidth: 500,
        sliderPercent: 50, // 第3张居中对应的百分比

        // ---- Slider state ----
        isDragging: false,
        dragStartX: 0,
        dragStartPercent: 0,

        // ---- Category Filters ----
        categories: [
          { id: 'all', nameKey: '全部案例' },
          { id: 'fashion', nameKey: '潮流服饰' },
          { id: 'home', nameKey: '家居生活' },
          { id: 'beauty', nameKey: '美妆护肤' },
        ],

        // ---- Featured Cases ----
        featuredCases: [
          {
            id: 1,
            category: 'beauty',
            tags: ['美妆护肤', '服装潮流'],
            title: '数码配件',
            descKey: '专注时尚配饰出口，极简风格设计，覆盖欧美年轻消费者。',
            linkKey: '查看店铺 →',
            rating: 4.7,
            ratingMax: 5,
            bgImage: require('@/assets/images/cases/FeaturedCases-1.png'),
            previewImage: require('@/assets/images/cases/FeaturedCases-2.png'),
            starImage: require('@/assets/images/cases/UserReviews-1.png'),
          },
        ],

        // ---- Rating Card Data ----
        ratingData: {
          icon: require('@/assets/images/cases/UserReviews-1.png'),
          starImage: require('@/assets/images/cases/UserReviews-star.png'),
          rating: 4.7,
          ratingMax: 5,
        },

        // ---- Testimonials ----
        testimonials: [
          {
            nameKey: '赵女士',
            quoteKey: '零基础小白也能轻松上手，从建站到出单全程有指引，客服响应超快，遇到问题几分钟就解决了，太安心了！',
            avatar: require('@/assets/images/cases/UserReviews-5.png'),
            stars: require('@/assets/images/cases/UserReviews-star.png'),
            quoteIcon: require('@/assets/images/cases/UserReviews-5-icon.png'),
            variant: 'primary',
          },
          {
            nameKey: '李女士',
            quoteKey: '多平台铺货 + AI翻译帮我省了一半时间，订单从零到日出百单只用了两个月，真的非常适合跨境新手。',
            avatar: require('@/assets/images/cases/UserReviews-3.png'),
            stars: require('@/assets/images/cases/UserReviews-star.png'),
            quoteIcon: require('@/assets/images/cases/UserReviews-3-icon.png'),
            variant: 'neutral',
          },
          {
            nameKey: '张先生',
            quoteKey: '以前用其他平台光是设置就要几天，Taoify 10分钟就建好了独立站，物流和收款一步到位，转化率提升特别明显！',
            avatar: require('@/assets/images/cases/UserReviews-2.png'),
            stars: require('@/assets/images/cases/UserReviews-star.png'),
            quoteIcon: require('@/assets/images/cases/UserReviews-2-icon.png'),
            variant: 'neutral',
          },
          {
            nameKey: '王先生',
            quoteKey: '从工厂转型DTC，Taoify的模板和营销工具帮了大忙，后台数据分析清晰，售后问题减少了一半！',
            avatar: require('@/assets/images/cases/UserReviews-4.png'),
            stars: require('@/assets/images/cases/UserReviews-star.png'),
            quoteIcon: require('@/assets/images/cases/UserReviews-4-icon.png'),
            variant: 'neutral',
          },
        ],

        // ---- State tracking ----
        imageErrors: {},

        // ---- Debounce timer ----
        filterTimer: null,
        ctaDebounceTimer: null,
      }
    },

    computed: {
      filteredCases () {
        if (this.activeCategory === 'all') {
          return this.featuredCases
        }
        return this.featuredCases.filter(c => c.category === this.activeCategory)
      },

      maxSliderPercent () {
        return 100
      },

      totalPreviewCount () {
        return this.previewImages.length
      },
    },

    mounted () {
      this.$nextTick(() => {
        // Delay to ensure layout complete before centering
        setTimeout(() => { this.scrollToPreview(2) }, 100)
        document.addEventListener('mousemove', this.onDragMove)
        document.addEventListener('mouseup', this.onDragEnd)
        document.addEventListener('touchmove', this.onDragMove, { passive: false })
        document.addEventListener('touchend', this.onDragEnd)
      })
    },

    beforeDestroy () {
      if (this.filterTimer) clearTimeout(this.filterTimer)
      if (this.ctaDebounceTimer) clearTimeout(this.ctaDebounceTimer)
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
      document.removeEventListener('touchmove', this.onDragMove)
      document.removeEventListener('touchend', this.onDragEnd)
    },

    methods: {
      // ---- Filter ----
      onFilterChange (catId) {
        if (this.filterTimer) clearTimeout(this.filterTimer)
        this.filterTimer = setTimeout(() => {
          this.activeCategory = catId
        }, 50)
      },

      // ---- CTA ----
      onCTA () {
        if (this.ctaDebounceTimer) return
        this.ctaDebounceTimer = setTimeout(() => {
          this.ctaDebounceTimer = null
        }, 300)
        this.$router.push('/sign-up').catch(() => {})
      },

      // ---- Image Error ----
      onImageError (key) {
        this.$set(this.imageErrors, key, true)
      },

      // ---- Preview Strip ----
      scrollToPreview (idx) {
        const strip = this.$refs.previewStrip
        if (!strip) return

        const totalCards = this.previewImages.length
        if (totalCards === 0) return

        const stripWidth = strip.clientWidth
        const cardWidth = this.cardBaseWidth
        const gap = 20 // matches CSS gap in track
        const cardStart = idx * (cardWidth + gap)
        const targetCenter = cardStart + cardWidth / 2
        const scrollTarget = targetCenter - stripWidth / 2
        const maxScroll = strip.scrollWidth - stripWidth

        strip.scrollTo({
          left: Math.max(0, Math.min(scrollTarget, maxScroll)),
          behavior: 'smooth',
        })

        this.activePreviewIdx = idx
        this.updateSliderFromScroll()
      },

      onStripScroll () {
        this.updateSliderFromScroll()
      },

      updateSliderFromScroll () {
        const strip = this.$refs.previewStrip
        if (!strip) return

        const maxScroll = strip.scrollWidth - strip.clientWidth
        if (maxScroll <= 0) {
          this.sliderPercent = 50
          return
        }
        this.sliderPercent = Math.round((strip.scrollLeft / maxScroll) * 100)
      },

      // ---- Preview Hover ----
      onPreviewHover (idx, event) {
        this.activePreviewIdx = idx
      },

      onPreviewLeave (idx) {
        // CSS scale + animation reset via :not(:hover)
      },

      // ---- Slider Navigation ----
      onSlidePrev () {
        const newIdx = Math.max(0, this.activePreviewIdx - 1)
        this.scrollToPreview(newIdx)
      },

      onSlideNext () {
        const newIdx = Math.min(this.previewImages.length - 1, this.activePreviewIdx + 1)
        this.scrollToPreview(newIdx)
      },

      // ---- Slider Drag ----
      onSliderDragStart (event) {
        const clientX = event.touches ? event.touches[0].clientX : event.clientX
        const track = this.$refs.sliderTrack
        if (!track) return

        const rect = track.getBoundingClientRect()
        const percent = ((clientX - rect.left) / rect.width) * 100
        this.applySliderPercent(Math.max(0, Math.min(100, percent)))
        this.isDragging = true
      },

      onThumbDragStart (event) {
        this.isDragging = true
        this.dragStartX = event.touches ? event.touches[0].clientX : event.clientX
        this.dragStartPercent = this.sliderPercent
      },

      onDragMove (event) {
        if (!this.isDragging) return

        const clientX = event.touches ? event.touches[0].clientX : event.clientX
        const track = this.$refs.sliderTrack
        if (!track) return

        const rect = track.getBoundingClientRect()
        const percent = ((clientX - rect.left) / rect.width) * 100
        this.applySliderPercent(Math.max(0, Math.min(100, percent)))
      },

      onDragEnd () {
        this.isDragging = false
      },

      applySliderPercent (percent) {
        this.sliderPercent = Math.round(percent)

        const strip = this.$refs.previewStrip
        if (!strip) return

        const maxScroll = strip.scrollWidth - strip.clientWidth
        if (maxScroll <= 0) return

        strip.scrollLeft = (percent / 100) * maxScroll

        // Update active index based on scroll position
        const cardWidth = this.cardBaseWidth
        const gap = 20
        const centerScroll = strip.scrollLeft + strip.clientWidth / 2
        const activeIdx = Math.floor(centerScroll / (cardWidth + gap))
        this.activePreviewIdx = Math.max(0, Math.min(this.previewImages.length - 1, activeIdx))
      },
    },
  }
</script>

<style scoped lang="scss">
/* ====== Design Tokens ====== */
.cases {
  --c-primary: #7331ff;
  --c-primary-dark: rgba(103, 91, 230, 1);
  --c-primary-shadow: rgba(103, 91, 230, 0.2);
  --c-primary-light: rgba(115, 49, 255, 0.1);
  --c-primary-bg: rgba(115, 49, 255, 0.08);
  --c-white: #ffffff;
  --c-text: #333333;
  --c-text-secondary: #666666;
  --c-text-muted: #999999;
  --c-text-dark: #000000;
  --c-text-gray: #4b5563;
  --c-text-slate: #374151;
  --c-bg-tab: #f2f2f2;
  --c-bg-neutral: #f8f8fe;
  --c-bg-light: #f7f7f7;
  --c-bg-page: #ffffff;
  --c-bg-mockup: #d9d9d9;
  --c-badge-bg: rgba(255, 255, 255, 0.6);
  --c-border: #ededed;
  --c-cta-start: #aa98fc;
  --c-cta-end: #7331ff;
  --c-stats-yellow: #feb301;
  --c-stats-yellow-light: rgba(254, 179, 1, 0.2);
  --c-stats-purple-light: rgba(103, 91, 230, 0.2);
  --c-track-bg: #e8e8e8;

  --r-sm: 4px;
  --r-md: 8px;
  --r-card: 24px;
  --r-btn: 34px;
  --r-pill: 47px;
  --r-tag: 48px;
  --r-circle: 50%;

  --shadow-card: 0px 4px 29px rgba(115, 49, 255, 0.05);
  --shadow-testimonial: 0px 0px 30px rgba(115, 49, 255, 0.15);
  --shadow-badge: 0px 1px 2px rgba(0, 0, 0, 0.05);

  --max-width: 1200px;
  --font-family: 'Source Han Sans CN', 'PingFang SC', 'Microsoft YaHei', 'Roboto', sans-serif;

  font-family: var(--font-family);
  background-color: var(--c-bg-page);
  overflow-x: hidden;
}

/* ====== 1. Hero Section ====== */
.cases-hero {
  width: 100%;
  height: 575px;
  background: url('~@/assets/images/cases/hero-bg.png') 100% no-repeat;
  background-size: cover;
  overflow: hidden;

  &__inner {
    max-width: 1313px;
    margin: 0 auto;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 26px 0 28px;
    height: 100%;
  }

  &__content {
    width: 573px;
    padding-top: 50px;
    flex-shrink: 0;
  }

  &__badge {
    display: inline-block;
    height: 32px;
    line-height: 32px;
    padding: 0 12px;
    background-color: var(--c-badge-bg);
    border-radius: var(--r-pill);
    color: var(--c-primary);
    font-size: 16px;
    letter-spacing: 1px;
    font-weight: normal;
    white-space: nowrap;
  }

  &__title {
    width: 573px;
    color: var(--c-text);
    font-size: 52px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 700;
    text-align: left;
    line-height: 72px;
    margin: 12px 0 0;
  }

  &__subtitle {
    width: 500px;
    color: var(--c-text);
    font-size: 14px;
    letter-spacing: 1px;
    font-weight: normal;
    text-align: left;
    white-space: nowrap;
    line-height: 14px;
    margin: 12px 0 0;
  }

  &__stat {
    display: inline-flex;
    align-items: center;
    box-shadow: var(--shadow-badge);
    background-color: var(--c-white);
    border-radius: var(--r-tag);
    width: 235px;
    height: 43px;
    margin-top: 44px;
    padding: 0 20px;
  }

  &__stat-icon {
    font-size: 24px;
    line-height: 1;
    flex-shrink: 0;
  }

  &__stat-num {
    color: var(--c-primary);
    font-size: 17px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
    line-height: 26px;
    margin-left: 8px;
  }

  &__stat-label {
    color: var(--c-text-slate);
    font-size: 16px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: normal;
    text-align: center;
    white-space: nowrap;
    line-height: 24px;
    margin-left: 2px;
  }

  &__visual {
    flex-shrink: 0;
    width: 668px;
    height: 476px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__visual-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

/* ====== 2. Section Header & Filter ====== */
.cases-section {
  max-width: var(--max-width);
  margin: 65px auto 0;
  text-align: center;

  &__title {
    color: var(--c-text);
    font-size: 32px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
    line-height: 48px;
  }

  &__subtitle {
    color: var(--c-text-muted);
    font-size: 20px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: normal;
    text-align: center;
    white-space: nowrap;
    line-height: 20px;
    margin-top: 12px;
  }
}

.cases-filter {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 40px;

  &__tab {
    width: 140px;
    height: 43px;
    border-radius: var(--r-btn);
    background-color: var(--c-bg-tab);
    border: none;
    color: var(--c-text);
    font-size: 18px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 500;
    text-align: center;
    white-space: nowrap;
    line-height: 43px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    outline: none;

    &:active {
      opacity: 0.6;
    }

    &--active {
      background-color: var(--c-primary);
      color: var(--c-white);

      &:active {
        opacity: 0.8;
      }
    }
  }
}

/* ====== 3. Preview Image Strip ====== */
.cases-preview {
  max-width: var(--max-width);
  margin: 40px auto 0;

  &__strip {
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    cursor: grab;

    &::-webkit-scrollbar { display: none; }

    &:active { cursor: grabbing; }
  }

  &__track {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 20px;
    padding: 0 calc(var(--max-width) * 0.15);
  }

  &__card {
    flex-shrink: 0;
    width: 360px;
    height: 600px;
    overflow: hidden;
    border-radius: var(--r-card);
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease;
    cursor: pointer;
    position: relative;

    &:hover {
      box-shadow: var(--shadow-testimonial);
      z-index: 2;
    }

    &--active {
      transform: scale(1.08);
      box-shadow: var(--shadow-testimonial);
      z-index: 2;
    }
  }

  &__card-inner {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__img {
    width: 100%;
    height: 130%;
    display: block;
    object-fit: cover;
    object-position: top;
  }

  &__card:hover &__img {
    animation: cases-scroll-down 12s ease-in-out 0.3s forwards;
  }

  &__card:not(:hover) &__img {
    animation: cases-scroll-up 0.3s ease-out forwards;
  }
}

@keyframes cases-scroll-down {
  0%   { object-position: top; }
  100% { object-position: bottom; }
}

@keyframes cases-scroll-up {
  0%   { object-position: bottom; }
  100% { object-position: top; }
}

/* ====== Slider Component ====== */
.cases-slider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding: 0 20px;
  user-select: none;

  &__arrow {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: var(--r-circle);
    border: 2px solid var(--c-primary);
    background: var(--c-white);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    outline: none;
    transition: background-color 0.2s ease;

    &:active {
      opacity: 0.6;
    }

    svg {
      width: 24px;
      height: 24px;
    }
  }

  &__track {
    flex: 1;
    max-width: 600px;
    height: 6px;
    background-color: var(--c-track-bg);
    border-radius: 3px;
    position: relative;
    cursor: pointer;
  }

  &__fill {
    height: 100%;
    background-color: var(--c-primary);
    border-radius: 3px;
    transition: width 0.15s ease;
  }

  &__thumb {
    position: absolute;
    top: 50%;
    width: 20px;
    height: 20px;
    border-radius: var(--r-circle);
    background-color: var(--c-primary);
    box-shadow: 0px 2px 6px rgba(115, 49, 255, 0.4);
    transform: translate(-50%, -50%);
    cursor: grab;
    transition: left 0.15s ease, box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0px 2px 12px rgba(115, 49, 255, 0.6);
    }

    &:active {
      cursor: grabbing;
      box-shadow: 0px 2px 16px rgba(115, 49, 255, 0.8);
    }
  }
}

/* ====== 4. Feedback Tagline ====== */
.cases-feedback {
  max-width: var(--max-width);
  margin: 60px auto 0;
  text-align: center;

  &__tagline {
    display: block;
    color: var(--c-text-muted);
    font-size: 20px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: normal;
    text-align: center;
    white-space: nowrap;
    line-height: 20px;
  }
}

/* ====== 6. User Reviews (独立用户评价板块，流式自适应) ====== */
.cases-reviews {
  max-width: var(--max-width);
  margin: 60px auto 0;

  &__heading {
    color: var(--c-text);
    font-size: 32px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
    line-height: 48px;
    margin-bottom: 24px;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
  }
}

.cases-review-card {
  flex: 0 1 380px;
  max-width: 456px;
  min-width: 320px;
  height: 238px;
  border-radius: var(--r-card);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 39px 40px;

  &--primary {
    background-color: var(--c-primary-light);
    border: 1px solid var(--c-primary);
    box-shadow: var(--shadow-testimonial);
  }

  &--neutral {
    background-color: var(--c-bg-neutral);
    border: none;
  }

  &--rating {
    background-color: rgba(255, 255, 255, 0.3);
    border: 2px solid rgba(115, 49, 255, 0.1);
    box-shadow: var(--shadow-card);
    align-items: center;
    padding: 40px;
  }

  &__header {
    display: flex;
    align-items: flex-start;
  }

  &__avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__name-row {
    margin-left: 12px;
    display: flex;
    flex-direction: column;
  }

  &__name {
    color: #1f2937;
    font-size: 16px;
    font-family: 'Segoe UI', sans-serif;
    font-weight: 700;
    white-space: nowrap;
    line-height: 24px;
  }

  &__stars {
    width: 116px;
    height: 20px;
    margin-top: 8px;
  }

  &__quote-icon {
    width: 32px;
    height: 32px;
    margin-left: auto;
    flex-shrink: 0;
  }

  &__quote {
    color: var(--c-text-secondary);
    font-size: 16px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 500;
    text-align: justify;
    line-height: 24px;
    margin-top: 28px;
  }

  /* Rating variant internals */
  &__rating-icon {
    font-size: 48px;
    line-height: 1;
  }

  &__rating-label {
    color: var(--c-text-secondary);
    font-size: 32px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 700;
    white-space: nowrap;
    margin-top: 12px;
  }

  &__rating-stars {
    display: flex;
    align-items: center;
    margin-top: 20px;
  }

  &__rating-star-img {
    width: 176px;
    height: 32px;
  }

  &__rating-num {
    color: var(--c-text-dark);
    font-size: 36px;
    font-family: 'Roboto', sans-serif;
    font-weight: 700;
    margin-left: 12px;
    line-height: 45px;
  }

  &__rating-max {
    color: var(--c-text-dark);
    font-size: 20px;
    font-family: 'Roboto', sans-serif;
    font-weight: normal;
    line-height: 45px;
    margin-left: 5px;
  }
}

/* Remove old testimonial classes (replaced by cases-review-card) */

/* ====== 7. CTA Section ====== */
.cases-cta {
  width: 100%;
  min-height: 212px;
  background: url('~@/assets/images/cases/CTASection-bg.png') 100% no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 140px;

  &__inner {
    text-align: center;
  }

  &__title {
    color: var(--c-text);
    font-size: 32px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 500;
    white-space: nowrap;
    line-height: 48px;
    margin: 0;
  }

  &__subtitle {
    color: var(--c-text-secondary);
    font-size: 20px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: normal;
    white-space: nowrap;
    line-height: 20px;
    margin-top: 8px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 180px;
    height: 54px;
    background: linear-gradient(180deg, var(--c-cta-start) 0%, var(--c-cta-end) 100%);
    border-radius: var(--r-btn);
    border: none;
    color: var(--c-white);
    font-size: 20px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: normal;
    line-height: 20px;
    cursor: pointer;
    margin-top: 16px;
    outline: none;
    white-space: nowrap;

    &:active {
      opacity: 0.6;
    }
  }

  &__btn-icon {
    font-size: 24px;
    line-height: 1;
  }
}

/* ====== Responsive: Tablet (≤1024px) ====== */
@media (max-width: 1024px) {
  .cases-hero {
    height: auto;
    min-height: 500px;

    &__inner {
      flex-direction: column;
      align-items: center;
      padding: 40px 20px 20px;
      max-width: 100%;
    }

    &__content {
      width: 100%;
      max-width: 600px;
      padding-top: 0;
    }

    &__title {
      width: 100%;
      font-size: 36px;
      line-height: 50px;
    }

    &__subtitle {
      width: 100%;
      white-space: normal;
    }

    &__visual {
      width: 100%;
      min-height: 300px;
      margin-top: 30px;
    }
  }

  .cases-preview {
    &__track {
      padding: 0 20px;
    }

    &__card {
      width: 280px !important;
      height: 460px;
    }
  }

  .cases-slider {
    &__track {
      max-width: 400px;
    }
  }

  .cases-review-card {
    width: 100%;
    max-width: 456px;
  }

  .cases-filter {
    flex-wrap: wrap;
    padding: 0 20px;
  }
}

/* ====== Responsive: Mobile (≤768px) ====== */
@media (max-width: 768px) {
  .cases-hero {
    &__title {
      font-size: 28px;
      line-height: 40px;
    }

    &__stat {
      width: auto;
      padding: 0 16px;
    }

    &__visual {
      min-height: 200px;
    }
  }

  .cases-preview {
    &__card {
      width: 240px !important;
      height: 400px;
    }
  }

  .cases-slider {
    gap: 8px;

    &__arrow {
      width: 32px;
      height: 32px;

      svg {
        width: 20px;
        height: 20px;
      }
    }

    &__track {
      max-width: 240px;
    }
  }

  .cases-section {
    margin-top: 30px;
    padding: 0 16px;

    &__title {
      font-size: 24px;
      line-height: 36px;
    }

    &__subtitle {
      font-size: 14px;
      white-space: normal;
    }
  }

  .cases-filter {
    gap: 8px;

    &__tab {
      width: auto;
      padding: 0 20px;
      font-size: 14px;
      height: 36px;
      line-height: 36px;
    }
  }

  .cases-reviews {
    &__heading {
      font-size: 24px;
      white-space: normal;
    }

    .cases-review-card--rating {
      max-width: 345px;
    }
  }

  .cases-review-card {
    width: 100%;
    height: auto;
    padding: 20px;

    &__quote {
      font-size: 14px;
    }
  }

  .cases-feedback__tagline {
    font-size: 16px;
    white-space: normal;
  }

  .cases-cta {
    margin-top: 60px;
    min-height: 200px;
    padding: 40px 16px;

    &__inner { padding: 0; }

    &__title {
      font-size: 24px;
      white-space: normal;
    }

    &__subtitle {
      font-size: 14px;
      white-space: normal;
    }

    &__btn {
      width: 150px;
      height: 44px;
      font-size: 16px;
    }
  }
}
</style>
